import React, { useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowDownTrayIcon, DocumentTextIcon, CheckCircleIcon, BoltIcon, ExclamationTriangleIcon, ArrowPathIcon, ShieldCheckIcon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2pdf from 'html2pdf.js';

const CatastropheSectionSummary = ({ title, data }) => {
  // Skip empty sections
  if (!data || Object.keys(data).length === 0) return null;
  
  // Extract key values from the section data
  const keyItems = Object.entries(data)
    .filter(([_, value]) => value && value !== '' && (typeof value !== 'object' || Object.keys(value).length > 0))
    .slice(0, 3); // Only show first 3 items
  
  if (keyItems.length === 0) return null;
  
  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--succession-text-primary)' }}>{title}</h3>
      <div className="rounded-md p-3" style={{ backgroundColor: 'var(--succession-bg-secondary)', border: '1px solid var(--succession-border)' }}>
        <ul className="text-sm space-y-1">
          {keyItems.map(([key, value]) => (
            <li key={key} style={{ color: 'var(--succession-text-secondary)' }}>
              {typeof value === 'string' ? (
                value.length > 60 ? `${value.substring(0, 60)}...` : value
              ) : (
                Array.isArray(value) ? `${value.length} items` : `${Object.keys(value).length} properties`
              )}
            </li>
          ))}
          {Object.keys(data).length > 3 && (
            <li className="italic" style={{ color: 'var(--succession-text-tertiary)' }}>+ {Object.keys(data).length - 3} more items</li>
          )}
        </ul>
      </div>
    </div>
  );
};

const InsightCard = ({ title, content, icon, bgColor, iconBgColor, textColor }) => {
  const Icon = icon;
  
  return (
    <div className={`${bgColor} overflow-hidden shadow rounded-lg`}>
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center">
          <div className={`flex-shrink-0 ${iconBgColor} rounded-md p-3`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <h3 className={`text-sm font-medium ${textColor}`}>{title}</h3>
            <p className={`mt-2 text-sm ${textColor} opacity-80`}>
              {content || "No insight available"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const CatastropheSummaryPage = ({ formData, previewContent, onRevise, aiInsights }) => {
  const pdfRef = useRef(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  // Debug logging for AI insights
  console.log("CatastropheSummaryPage received aiInsights:", aiInsights);
  
  // Extract sections with data
  const completedSections = Object.entries(formData || {})
    .filter(([_, sectionData]) => sectionData && Object.keys(sectionData).length > 0);
  
  // Calculate completion metrics
  const totalSections = 17; // This should match the total number of sections in the schema
  const completedSectionCount = completedSections.length;
  
  // Ensure completion percentage doesn't exceed 100%
  const completionPercentage = Math.min(100, Math.round((completedSectionCount / totalSections) * 100));

  const handleDownloadPDF = () => {
    const element = pdfRef.current;
    if (!element) return;
    
    const opt = {
      margin: 0.5,
      filename: 'business-catastrophe-plan.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    
    // Show loading state
    const loadingNotification = document.createElement('div');
    loadingNotification.className = 'fixed bottom-4 right-4 bg-succession-primary text-white py-2 px-4 rounded-md shadow-succession-md transition-colors duration-300';
    loadingNotification.textContent = 'Generating PDF...';
    document.body.appendChild(loadingNotification);
    
    html2pdf().set(opt).from(element).save().then(() => {
      // Remove loading state on success
      document.body.removeChild(loadingNotification);
    }).catch((error) => {
      console.error('PDF generation failed:', error);
      document.body.removeChild(loadingNotification);
      
      // Show error notification
      const errorNotification = document.createElement('div');
      errorNotification.className = 'fixed bottom-4 right-4 bg-succession-error text-white py-2 px-4 rounded-md shadow-succession-md transition-colors duration-300';
      errorNotification.textContent = 'PDF generation failed. Please try again.';
      document.body.appendChild(errorNotification);
      
      // Remove error notification after 3 seconds
      setTimeout(() => {
        if (document.body.contains(errorNotification)) {
          document.body.removeChild(errorNotification);
        }
      }, 3000);
    });
  };

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: 'var(--succession-bg-secondary)' }}>
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold" style={{ color: 'var(--succession-text-primary)' }}>Business Catastrophe Plan Summary</h1>
                </div>
                <button
                  onClick={onRevise}
                  className="inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{ borderColor: 'var(--succession-border)', color: 'var(--succession-text-secondary)', backgroundColor: 'var(--succession-bg-primary)', focusRingColor: 'var(--succession-primary)' }}
                >
                  <ArrowLeftIcon className="h-5 w-5 mr-2" />
                  Back to Editor
                </button>
              </div>
              
              {/* Prominent Completion Banner */}
              <div className="mb-8 border rounded-lg shadow-md overflow-hidden" style={{ backgroundColor: 'linear-gradient(to right, var(--succession-success-bg), var(--succession-success-bg))', borderColor: 'var(--succession-success-border)' }}>
                <div className="px-6 py-5 sm:px-8 sm:py-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between">
                    <div className="mb-4 sm:mb-0 text-center sm:text-left">
                      <div className="flex items-center mb-2">
                        <CheckCircleIcon className="h-6 w-6 mr-2" style={{ color: 'var(--succession-success)' }} />
                        <h2 className="text-xl font-bold" style={{ color: 'var(--succession-success)' }}>Congratulations!</h2>
                      </div>
                      <p className="text-lg font-medium" style={{ color: 'var(--succession-success)' }}>Your Business Catastrophe Plan is complete.</p>
                      <p className="mt-1 text-sm" style={{ color: 'var(--succession-success)' }}>
                        You've successfully created a comprehensive plan to protect your business from catastrophic events.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={onRevise}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ backgroundColor: 'var(--succession-primary)', focusRingColor: 'var(--succession-primary)' }}
                      >
                        <DocumentTextIcon className="h-5 w-5 mr-2" />
                        Revise Plan
                      </button>
                      <button
                        onClick={() => setShowPreviewModal(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ backgroundColor: 'var(--succession-primary)', focusRingColor: 'var(--succession-primary)' }}
                      >
                        <EyeIcon className="h-5 w-5 mr-2" />
                        Preview Plan
                      </button>
                      <button
                        onClick={handleDownloadPDF}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ backgroundColor: 'var(--succession-success)', focusRingColor: 'var(--succession-success)' }}
                      >
                        <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                        Download PDF
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="shadow overflow-hidden sm:rounded-lg mb-8" style={{ backgroundColor: 'var(--succession-bg-primary)', border: '1px solid var(--succession-border)' }}>
                <div className="px-4 py-5 sm:px-6" style={{ backgroundColor: 'var(--succession-bg-secondary)' }}>
                  <h2 className="text-lg font-medium" style={{ color: 'var(--succession-text-primary)' }}>Plan Overview</h2>
                  <p className="mt-1 max-w-2xl text-sm" style={{ color: 'var(--succession-text-secondary)' }}>
                    {completionPercentage < 100 && 
                      `Your Business Catastrophe Plan is ${completionPercentage}% complete (${completedSectionCount} of ${totalSections} sections).`
                    }
                  </p>
                </div>
                <div className="border-t px-4 py-5 sm:p-6" style={{ borderColor: 'var(--succession-border)' }}>
                  {/* AI Insights Section */}
                  {aiInsights && (
                    <div>
                      <h3 className="text-lg font-medium mb-4 flex items-center" style={{ color: 'var(--succession-text-primary)' }}>
                        <BoltIcon className="h-5 w-5 mr-2" style={{ color: 'var(--succession-primary)' }} />
                        AI-Generated Insights
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InsightCard 
                          title="Key Strengths"
                          content={aiInsights.key_strengths}
                          icon={CheckCircleIcon}
                          bgColor="bg-green-50"
                          iconBgColor="bg-green-500"
                          textColor="text-green-800"
                        />
                        
                        <InsightCard 
                          title="Identified Risks"
                          content={aiInsights.identified_risks}
                          icon={ExclamationTriangleIcon}
                          bgColor="bg-yellow-50"
                          iconBgColor="bg-yellow-500"
                          textColor="text-yellow-800"
                        />
                        
                        <InsightCard 
                          title="Action Recommendations"
                          content={aiInsights.action_recommendations}
                          icon={ArrowPathIcon}
                          bgColor="bg-blue-50"
                          iconBgColor="bg-blue-500"
                          textColor="text-blue-800"
                        />
                        
                        <InsightCard 
                          title="Compliance Status"
                          content={aiInsights.compliance_status}
                          icon={ShieldCheckIcon}
                          bgColor="bg-purple-50"
                          iconBgColor="bg-purple-500"
                          textColor="text-purple-800"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Preview Modal */}
              {showPreviewModal && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                  <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    {/* Background overlay */}
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowPreviewModal(false)}></div>
                    
                    {/* Modal panel */}
                    <div className="inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" style={{ backgroundColor: 'var(--succession-bg-primary)' }}>
                      <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4" style={{ backgroundColor: 'var(--succession-bg-primary)' }}>
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg leading-6 font-medium" style={{ color: 'var(--succession-text-primary)' }} id="modal-title">
                            Business Catastrophe Plan Preview
                          </h3>
                          <button
                            onClick={() => setShowPreviewModal(false)}
                            className="hover:opacity-80"
                            style={{ color: 'var(--succession-text-tertiary)' }}
                          >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-6 w-6" />
                          </button>
                        </div>
                        <div className="rounded-md p-4 max-h-[70vh] overflow-y-auto" style={{ backgroundColor: 'var(--succession-bg-secondary)' }}>
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {previewContent || '# Business Catastrophe Plan\n\nNo preview available.'}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </div>
                      <div className="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" style={{ backgroundColor: 'var(--succession-bg-secondary)' }}>
                        <button
                          onClick={() => setShowPreviewModal(false)}
                          className="mt-3 w-full inline-flex justify-center rounded-md border shadow-sm px-4 py-2 text-base font-medium hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                          style={{ borderColor: 'var(--succession-border)', backgroundColor: 'var(--succession-bg-primary)', color: 'var(--succession-text-secondary)', focusRingColor: 'var(--succession-primary)' }}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Hidden div for PDF generation */}
              <div className="hidden">
                <div ref={pdfRef} className="p-8" style={{ width: '8.5in', maxWidth: '8.5in', fontFamily: 'Arial, sans-serif', backgroundColor: 'var(--succession-bg-primary)' }}>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <h1 className="text-3xl font-bold mb-2">Business Catastrophe Plan</h1>
                    <p style={{ color: 'var(--succession-text-secondary)' }}>Generated on {new Date().toLocaleDateString()}</p>
                  </div>
                  
                  <hr className="my-4" style={{ borderColor: 'var(--succession-border)' }} />
                  
                  {/* AI Insights for PDF */}
                  {aiInsights && (
                    <div style={{ marginBottom: '20px' }}>
                      <h2 style={{ fontSize: '18pt', marginBottom: '10px' }}>AI-Generated Insights</h2>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--succession-success)' }}>Key Strengths</h3>
                        <p className="text-sm" style={{ color: 'var(--succession-success)' }}>{aiInsights.key_strengths}</p>
                      </div>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--succession-warning)' }}>Identified Risks</h3>
                        <p className="text-sm" style={{ color: 'var(--succession-warning)' }}>{aiInsights.identified_risks}</p>
                      </div>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--succession-primary)' }}>Action Recommendations</h3>
                        <p className="text-sm" style={{ color: 'var(--succession-primary)' }}>{aiInsights.action_recommendations}</p>
                      </div>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--succession-secondary)' }}>Compliance Status</h3>
                        <p className="text-sm" style={{ color: 'var(--succession-secondary)' }}>{aiInsights.compliance_status}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="prose max-w-none" style={{ fontSize: '12pt', lineHeight: '1.5' }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {previewContent || '# Business Catastrophe Plan\n\nNo content available.'}
                    </ReactMarkdown>
                  </div>
                  
                  <div className="mt-8 text-xs text-center" style={{ color: 'var(--succession-text-tertiary)' }}>
                    <p>This document was automatically generated from the Business Catastrophe Plan tool.</p>
                    <p>© {new Date().getFullYear()} FindBob. All rights reserved.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CatastropheSummaryPage; 